import React from "react";
import { Dialog, DialogContent } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import { Transition } from "../Calendars/Calendars";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { Dispatcher } from "../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { TableHeadComponent } from "../../common/TableHead";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import FormHelperText from "@mui/material/FormHelperText";
import Checkbox from "@mui/material/Checkbox";
import { ThemeProvider } from "@emotion/react";
import { theme } from "../AddSite/StepsStepper";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import { TextField, Autocomplete } from "@mui/material";
import { handleSaveChangesUserInfo } from "./GroupUsersUtils/handleSaveChangesUserInfo";
import { NavigateFunction } from "react-router-dom";
import { useAppStore } from "../../../appStore";
import { logger } from "../../../utils/logger";
import { VisibleScreensSelect } from "./VisibleScreensSelect";
import {
  ScreenOption,
  ALARM_REVIEW_ACCESS_LEVEL,
  VIEW_ONLY_ACCESS_LEVEL,
  VISIBLE_SCREENS_FIELD,
  describeVocabularyError,
  fetchScreenVocabulary,
  isViewOnly,
  readVisibleScreens,
  selectableScreens,
  withVisibleScreensForLevel,
} from "../../utils/viewOnlyAccess";

export const AboutUserDialog = ({
  aboutUserDialogOpen,
  setAboutUserDialogOpen,
  row,
  selectedRowIndex,
  setGroupUserData,
  setUpdatingGroupUserData,
  setAlertState,
  setActiveData,
  navigate,
}: {
  aboutUserDialogOpen: boolean;
  setAboutUserDialogOpen: Dispatcher<boolean>;
  row: any;
  setGroupUserData: any;
  setUpdatingGroupUserData: Dispatcher<boolean>;
  setAlertState: any;
  setActiveData: any;
  navigate: NavigateFunction;
  selectedRowIndex: number;
}) => {
  const [selectedRow, setSelectedRow] = React.useState(row);

  const [editMode, setEditMode] = React.useState(false);

  const [changesMade, setChangesMade] = React.useState(false);

  const [nameChangesMade, setNameChangesMade] = React.useState(false);

  const parentAccounts = JSON.parse(localStorage.parentAccounts);

  const userID = selectedRow.user.id;

  const parentIds = new Map();
  const parentNames = new Map();
  parentAccounts.forEach((object: any) => {
    parentIds.set(object.name, object.id);
  });
  parentAccounts.forEach((object: any) => {
    parentNames.set(object.id, object.name);
  });

  const backupSelectedRow = row;

  const [patchObject, setPatchObject] = React.useState<any>({});

  const [namePatchObject, setNamePatchObject] = React.useState<any>({});

  // ENG-1815: View Only screen picker. Only editors who manage users get the
  // control; everyone else sees the dialog exactly as before.
  const { userPermissions } = useAppStore();
  const canManageUsers = Boolean(userPermissions?.manage_users);

  const savedVisibleScreens = readVisibleScreens(row);
  const [visibleScreens, setVisibleScreens] =
    React.useState<string[]>(savedVisibleScreens);
  const [screenVocabulary, setScreenVocabulary] = React.useState<
    ScreenOption[]
  >([]);
  const [vocabularyError, setVocabularyError] = React.useState<string | null>(
    null
  );

  React.useEffect(() => {
    if (!canManageUsers) return;
    let cancelled = false;
    fetchScreenVocabulary().then(
      (options) => {
        if (!cancelled) {
          setScreenVocabulary(options);
          setVocabularyError(null);
        }
      },
      (reason) => {
        // Without the vocabulary the picker offers View All only; the rest of
        // the dialog is unaffected. Surface the status so an empty list is
        // explained rather than silent.
        logger.warn("Could not load the visible-screens vocabulary:", reason);
        if (!cancelled) setVocabularyError(describeVocabularyError(reason));
      }
    );
    return () => {
      cancelled = true;
    };
  }, [canManageUsers]);

  // Health / Patrols / WireGuard are offered only when this user's row says
  // they have CHM sites / patrol sites / tunnels.
  const screenOptions = selectableScreens(screenVocabulary, row);

  // The radio group is controlled off the pending patch so the picker
  // appears/disappears as the level changes, not only on reopen.
  const currentAccessLevel =
    patchObject.access_level ?? selectedRow.access_level;

  const screensChanged =
    JSON.stringify(visibleScreens) !== JSON.stringify(savedVisibleScreens);

  const selectAccessLevel = (level: number) => {
    setChangesMade(true);
    setPatchObject((previousState: any) => {
      const next = { ...previousState, access_level: level };
      if (isViewOnly(level)) {
        if (screensChanged) next[VISIBLE_SCREENS_FIELD] = visibleScreens;
        return next;
      }
      return withVisibleScreensForLevel(next, level);
    });
  };

  const selectVisibleScreens = (next: string[]) => {
    setChangesMade(true);
    setVisibleScreens(next);
    setPatchObject((previousState: any) => ({
      ...previousState,
      [VISIBLE_SCREENS_FIELD]: next,
    }));
  };

  // Rendered under the selected read-only radio (View Only or Alarm Review).
  const screenPicker =
    canManageUsers && isViewOnly(currentAccessLevel) ? (
      <div className="pl-[32px] pb-[8px]">
        <VisibleScreensSelect
          value={visibleScreens}
          onChange={selectVisibleScreens}
          options={screenOptions}
          disabled={!editMode}
          loadError={vocabularyError}
        />
      </div>
    ) : null;

  const discardEdits = () => {
    setSelectedRow(backupSelectedRow);
    setVisibleScreens(savedVisibleScreens);
    setPatchObject({});
    setNamePatchObject({});
    setChangesMade(false);
    setNameChangesMade(false);
    setEditMode(false);
  };

  return (
    <div className="absolute">
      <Dialog
        open={aboutUserDialogOpen}
        TransitionComponent={Transition}
        fullScreen
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar sx={{ backgroundColor: "#001943" }}>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => {
                setAboutUserDialogOpen(false);
              }}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              User Info
            </Typography>
          </Toolbar>
        </AppBar>
        <DialogContent>
          {!editMode && (
            <div className="absolute right-[25px] top-[80px]">
              <OrangeTextButton
                onClick={() => {
                  setEditMode(true);
                }}
                text={
                  <>
                    <ModeEditIcon />
                    &nbsp;Edit
                  </>
                }
                disabled={false}
              />
            </div>
          )}
          {editMode && (
            <div className="absolute right-[180px] top-[80px]">
              <OrangeTextButton
                onClick={discardEdits}
                text="Cancel"
                disabled={false}
              />
            </div>
          )}
          {editMode && (
            <div className="absolute right-[25px] top-[80px]">
              <OrangeTextButton
                onClick={() => {
                  setUpdatingGroupUserData(true);
                  setAboutUserDialogOpen(false);
                  handleSaveChangesUserInfo(
                    selectedRow.id,
                    selectedRowIndex,
                    changesMade,
                    // The screen list rides along only for View Only.
                    withVisibleScreensForLevel(patchObject, currentAccessLevel),
                    setGroupUserData,
                    setUpdatingGroupUserData,
                    setAlertState,
                    nameChangesMade,
                    namePatchObject,
                    userID,
                    setActiveData,
                    navigate
                  );
                }}
                text="Save Changes"
                disabled={!changesMade && !nameChangesMade}
              />
            </div>
          )}
          <p className="font-mulish text-[20px] font-bold">General Info</p>
          <TableContainer>
            <Table sx={{ fontFamily: "Mulish", width: "100%" }}>
              <TableHeadComponent
                titlesArray={[
                  "Group",
                  "Username",
                  "First Name",
                  "Last Name",
                  "Email",
                ]}
              />
              <TableBody>
                <TableRow>
                  <TableCell
                    align="left"
                    sx={{ fontFamily: "Mulish", width: 250 }}
                  >
                    {!editMode ? (
                      selectedRow.group.name
                    ) : (
                      <Autocomplete
                        disablePortal
                        options={
                          parentAccounts[0]
                            ? parentAccounts.map(
                                (object: any, index: number) => ({
                                  id: object.id,
                                  name: object.name,
                                  index: index,
                                })
                              )
                            : []
                        }
                        getOptionLabel={(option) => option.name || ""}
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        value={
                          selectedRow.group.id
                            ? {
                                id: selectedRow.group.id,
                                name: selectedRow.group.name,
                                index: parentAccounts.findIndex(
                                  (obj: any) => obj.id === selectedRow.group.id
                                ),
                              }
                            : null
                        }
                        onChange={(event, value: any) => {
                          if (value) {
                            setChangesMade(true);
                            setSelectedRow((previousState: any) => {
                              return {
                                ...previousState,
                                group: {
                                  id: value.id,
                                  name: value.name,
                                },
                              };
                            });
                            setPatchObject((previousState: any) => {
                              return {
                                ...previousState,
                                group: value.id,
                              };
                            });
                          }
                        }}
                        sx={{ width: 250 }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="standard"
                            label="Group"
                            autoComplete="off"
                            id="group_name"
                            name="group_name"
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id}>
                            {option.name}
                          </li>
                        )}
                      />
                    )}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ fontFamily: "Mulish", width: 250 }}
                  >
                    {
                      // !editMode ? (
                      selectedRow.user.username
                      // ) : (
                      //   <TextField
                      //     variant="standard"
                      //     value={selectedRow.user.username}
                      //     onChange={(event) => {
                      //       setChangesMade(true);
                      //       setSelectedRow((previousState: any) => {
                      //         const updatedUser = previousState.user;
                      //         updatedUser.username = event.target.value;
                      //         return { ...previousState, user: updatedUser };
                      //       });
                      //       setPatchObject((previousState: any) => {
                      //         return {
                      //           ...previousState,
                      //           username: event.target.value,
                      //         };
                      //       });
                      //     }}
                      //     sx={{ width: 250 }}
                      //     helperText=""
                      //     type="string"
                      //   />
                      // )
                    }
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ fontFamily: "Mulish", width: 250 }}
                  >
                    {!editMode ? (
                      selectedRow.user.first_name
                    ) : (
                      <TextField
                        variant="standard"
                        value={selectedRow.user.first_name}
                        onChange={(event) => {
                          setNameChangesMade(true);
                          setSelectedRow((previousState: any) => {
                            const updatedUser = previousState.user;
                            updatedUser.first_name = event.target.value;
                            return { ...previousState, user: updatedUser };
                          });
                          setNamePatchObject((previousState: any) => {
                            return {
                              ...previousState,
                              first_name: event.target.value,
                            };
                          });
                        }}
                        sx={{ width: 250 }}
                        helperText=""
                        type="string"
                      />
                    )}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ fontFamily: "Mulish", width: 250 }}
                  >
                    {!editMode ? (
                      selectedRow.user.last_name
                    ) : (
                      <TextField
                        variant="standard"
                        value={selectedRow.user.last_name}
                        onChange={(event) => {
                          setNameChangesMade(true);
                          setSelectedRow((previousState: any) => {
                            const updatedUser = previousState.user;
                            updatedUser.last_name = event.target.value;
                            return { ...previousState, user: updatedUser };
                          });
                          setNamePatchObject((previousState: any) => {
                            return {
                              ...previousState,
                              last_name: event.target.value,
                            };
                          });
                        }}
                        sx={{ width: 250 }}
                        helperText=""
                        type="string"
                      />
                    )}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ fontFamily: "Mulish", width: 250 }}
                  >
                    {selectedRow.user.email}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <br></br>
          <br></br>
          <ThemeProvider theme={theme}>
            <div className="font-mulish flex columns-2">
              <div key="access_level" className="w-[50%]">
                <p className="font-mulish text-[20px] font-bold">
                  Access Level
                </p>
                <div className="pl-[10px] py-[10px]">
                  <FormControl>
                    <RadioGroup value={currentAccessLevel}>
                      <FormControlLabel
                        onClick={() => {
                          if (editMode)
                            selectAccessLevel(VIEW_ONLY_ACCESS_LEVEL);
                        }}
                        value={VIEW_ONLY_ACCESS_LEVEL}
                        control={<Radio />}
                        label="View Only"
                        disabled={!editMode}
                      />
                      {/* The screen picker sits directly under whichever
                          read-only radio is selected, so they read as one
                          choice. */}
                      {Number(currentAccessLevel) === VIEW_ONLY_ACCESS_LEVEL &&
                        screenPicker}
                      {/* ENG-2010: View Only plus setting alert outcomes. The
                          screen picker applies to both read-only levels. */}
                      <FormControlLabel
                        onClick={() => {
                          if (editMode)
                            selectAccessLevel(ALARM_REVIEW_ACCESS_LEVEL);
                        }}
                        value={ALARM_REVIEW_ACCESS_LEVEL}
                        control={<Radio />}
                        label="Alarm Review (View Only + set alert outcomes)"
                        disabled={!editMode}
                      />
                      {Number(currentAccessLevel) ===
                        ALARM_REVIEW_ACCESS_LEVEL && screenPicker}
                      <FormControlLabel
                        onClick={() => {
                          if (editMode) selectAccessLevel(1);
                        }}
                        value={1}
                        control={<Radio />}
                        label="Arm and Disarm Only"
                        disabled={!editMode}
                      />
                      <FormControlLabel
                        onClick={() => {
                          if (editMode) selectAccessLevel(2);
                        }}
                        value={2}
                        control={<Radio />}
                        label="Manage Existing Cameras"
                        disabled={!editMode}
                      />
                      <FormControlLabel
                        onClick={() => {
                          if (editMode) selectAccessLevel(3);
                        }}
                        value={3}
                        control={<Radio />}
                        label="Self-Service (can add new cameras)"
                        disabled={!editMode}
                      />
                      <FormControlLabel
                        onClick={() => {
                          if (editMode) selectAccessLevel(4);
                        }}
                        value={4}
                        control={<Radio />}
                        label="Self-Service with Delete Permissions"
                        disabled={!editMode}
                      />
                    </RadioGroup>
                  </FormControl>
                </div>
              </div>
              <div key="permissions" className="w-[50%]">
                <p className="font-mulish text-[20px] font-bold">Permissions</p>
                <div className="pl-[10px] py-[10px]">
                  <div>
                    <FormControl variant="standard">
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="primary"
                              checked={selectedRow.create_user}
                              onChange={(event, checked) => {
                                setChangesMade(true);
                                setSelectedRow((previousState: any) => {
                                  return {
                                    ...previousState,
                                    create_user: checked,
                                  };
                                });
                                setPatchObject((previousState: any) => {
                                  return {
                                    ...previousState,
                                    create_user: checked,
                                  };
                                });
                              }}
                              disabled={!editMode}
                            />
                          }
                          label="Create User"
                        />
                      </FormGroup>
                      <FormHelperText>
                        User can create other users under their group
                      </FormHelperText>
                    </FormControl>
                  </div>
                  <div className="pt-[10px]">
                    <FormControl variant="standard">
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="primary"
                              checked={selectedRow.clips}
                              onChange={(event, checked) => {
                                setChangesMade(true);
                                setSelectedRow((previousState: any) => {
                                  return { ...previousState, clips: checked };
                                });
                                setPatchObject((previousState: any) => {
                                  return { ...previousState, clips: checked };
                                });
                              }}
                              disabled={!editMode}
                            />
                          }
                          label="Clips / AI Link"
                        />
                      </FormGroup>
                      <FormHelperText>
                        User can view Clips / AI Link
                      </FormHelperText>
                    </FormControl>
                  </div>
                  <div className="pt-[10px]">
                    <FormControl variant="standard">
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="primary"
                              checked={selectedRow.view_empty_group}
                              onChange={(event, checked) => {
                                setChangesMade(true);
                                setSelectedRow((previousState: any) => {
                                  return {
                                    ...previousState,
                                    view_empty_group: checked,
                                  };
                                });
                                setPatchObject((previousState: any) => {
                                  return {
                                    ...previousState,
                                    view_empty_group: checked,
                                  };
                                });
                              }}
                              disabled={!editMode}
                            />
                          }
                          label="Show Empty Group Folders"
                        />
                      </FormGroup>
                      <FormHelperText>
                        {`User can view empty group folders on the Site's page.`}
                      </FormHelperText>
                    </FormControl>
                  </div>
                  <div className="pt-[10px]">
                    <FormControl variant="standard">
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="primary"
                              checked={selectedRow.view_passwords}
                              onChange={(event, checked) => {
                                setChangesMade(true);
                                setSelectedRow((previousState: any) => {
                                  return {
                                    ...previousState,
                                    view_passwords: checked,
                                  };
                                });
                                setPatchObject((previousState: any) => {
                                  return {
                                    ...previousState,
                                    view_passwords: checked,
                                  };
                                });
                              }}
                              disabled={!editMode}
                            />
                          }
                          label="View Passwords"
                        />
                      </FormGroup>
                      <FormHelperText>
                        {`User can view passwords. We recommend this be enabled if this user will be tasked with camera onboarding.`}
                      </FormHelperText>
                    </FormControl>
                  </div>
                  <div className="pt-[10px]">
                    <FormControl variant="standard">
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="primary"
                              checked={selectedRow.autopatrol}
                              onChange={(event, checked) => {
                                setChangesMade(true);
                                setSelectedRow((previousState: any) => {
                                  return {
                                    ...previousState,
                                    autopatrol: checked,
                                  };
                                });
                                setPatchObject((previousState: any) => {
                                  return {
                                    ...previousState,
                                    autopatrol: checked,
                                  };
                                });
                              }}
                              disabled={!editMode}
                            />
                          }
                          label="Auto Patrol / Visual Camera Health"
                        />
                      </FormGroup>
                      <FormHelperText>
                        {`Access to views specific to Auto Patrol / Visual Camera Health.`}
                      </FormHelperText>
                    </FormControl>
                  </div>
                  <div className="pt-[10px]">
                    <FormControl variant="standard">
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="primary"
                              checked={selectedRow.alert_muting}
                              onChange={(event, checked) => {
                                setChangesMade(true);
                                setSelectedRow((previousState: any) => {
                                  return {
                                    ...previousState,
                                    alert_muting: checked,
                                  };
                                });
                                setPatchObject((previousState: any) => {
                                  return {
                                    ...previousState,
                                    alert_muting: checked,
                                  };
                                });
                              }}
                              disabled={!editMode}
                            />
                          }
                          label="View Alert Muting"
                        />
                      </FormGroup>
                      <FormHelperText>
                        User can mute alerts from their triage on the Alerts
                        page
                      </FormHelperText>
                    </FormControl>
                  </div>
                  <div className="pt-[10px]">
                    <FormControl variant="standard">
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="primary"
                              checked={selectedRow.live_streaming}
                              onChange={(event, checked) => {
                                setChangesMade(true);
                                setSelectedRow((previousState: any) => {
                                  return {
                                    ...previousState,
                                    live_streaming: checked,
                                  };
                                });
                                setPatchObject((previousState: any) => {
                                  return {
                                    ...previousState,
                                    live_streaming: checked,
                                  };
                                });
                              }}
                              disabled={!editMode}
                            />
                          }
                          label="Live Streaming"
                        />
                      </FormGroup>
                      <FormHelperText>
                        User can view live camera streams from the Alerts page
                      </FormHelperText>
                    </FormControl>
                  </div>
                  <div>
                    <FormControl variant="standard">
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="primary"
                              checked={selectedRow.create_wireguard_tunnels}
                              onChange={(event, checked) => {
                                setChangesMade(true);
                                setSelectedRow((previousState: any) => {
                                  return {
                                    ...previousState,
                                    create_wireguard_tunnels: checked,
                                  };
                                });
                                setPatchObject((previousState: any) => {
                                  return {
                                    ...previousState,
                                    create_wireguard_tunnels: checked,
                                  };
                                });
                              }}
                              disabled={!editMode}
                            />
                          }
                          label="Create WireGuard Tunnels"
                        />
                      </FormGroup>
                      <FormHelperText>
                        User can create and manage WireGuard tunnels
                      </FormHelperText>
                    </FormControl>
                  </div>
                  {/* <div>
                    <FormControl variant="standard">
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="primary"
                              checked={selectedRow.remote_link}
                              onChange={(event, checked) => {
                                setChangesMade(true);
                                setSelectedRow((previousState: any) => {
                                  return {
                                    ...previousState,
                                    remote_link: checked,
                                  };
                                });
                                setPatchObject((previousState: any) => {
                                  return {
                                    ...previousState,
                                    remote_link: checked,
                                  };
                                });
                              }}
                              disabled={!editMode}
                            />
                          }
                          label="Remote Link"
                        />
                      </FormGroup>
                      <FormHelperText>
                      Access to views specific to Immix Remote Link
                      </FormHelperText>
                    </FormControl>
                  </div> */}
                </div>
              </div>
            </div>
          </ThemeProvider>
        </DialogContent>
      </Dialog>
    </div>
  );
};
